import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import { useAuth } from '../context/AuthContext'
import * as XLSX from 'xlsx'
import PrintDocument from './PrintDocument'
import { resolveHierarchyKey, buildCostCodeMaps } from './budgetVsCommitment'

// ── Approval stages ───────────────────────────────────────────
const STAGE_CONFIG = {
  draft:        { label: 'Draft',              bg: 'bg-gray-100',    text: 'text-gray-600' },
  site:         { label: 'Pending Site',        bg: 'bg-blue-100',    text: 'text-blue-700' },
  cost_control: { label: 'Pending Cost Control',bg: 'bg-amber-100',   text: 'text-amber-700' },
  procurement:  { label: 'Pending Procurement', bg: 'bg-purple-100',  text: 'text-purple-700' },
  approved:     { label: 'Approved',            bg: 'bg-green-100',   text: 'text-green-700' },
  rejected:     { label: 'Rejected',            bg: 'bg-red-100',     text: 'text-red-700' },
  converted:    { label: 'Converted',           bg: 'bg-indigo-100',  text: 'text-indigo-700' },
}

const PRIORITY_CONFIG = {
  low:    { label: 'Low',    color: 'text-gray-400' },
  normal: { label: 'Normal', color: 'text-blue-500' },
  high:   { label: 'High',   color: 'text-amber-500' },
  urgent: { label: 'Urgent', color: 'text-red-500' },
}

// Temp roles for testing (will be replaced by real auth later)
const ROLES = ['site_engineer', 'cost_control', 'procurement', 'director']
const ROLE_LABELS = {
  site_engineer: 'Site Engineer',
  cost_control:  'Cost Control',
  procurement:   'Procurement',
  director:      'Director',
}

function fmtN(n) {
  if (!n && n !== 0) return '—'
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}
// Phase B: shared normalization helper, matching the LOWER(TRIM()) standard
// already established by the live get_commitment_by_subcode() RPC.
const normSC = s => (s || '').toString().trim().toLowerCase()

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t) }, [onClose])
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white
      ${type === 'error' ? 'bg-red-600' : 'bg-[#1A7A4A]'}`}>
      {message}
    </div>
  )
}

function StageBadge({ stage }) {
  const c = STAGE_CONFIG[stage] || STAGE_CONFIG.draft
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>{c.label}</span>
}

// ── Confirm Overlay ───────────────────────────────────────────
const ConfirmOverlay = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/40" onClick={onCancel}/>
    <div className="relative bg-white rounded-xl shadow-2xl p-6 w-80">
      <p className="text-sm text-[#1C1A18] mb-5">{message}</p>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6]">Cancel</button>
        <button onClick={onConfirm} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Confirm</button>
      </div>
    </div>
  </div>
)

const EMPTY_LINE = { description: '', unit: '', est_qty: '', sub_code: '', notes: '' }

export default function PurchaseRequisitions({ initProject }) {
  const [activeTab, setActiveTab]             = useState('material') // material | equipment
  const [projects, setProjects]               = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [prs, setPrs]                         = useState([])
  const [costCodes, setCostCodes]             = useState([])
  const [budgetSubCodes, setBudgetSubCodes]   = useState([]) // from budget_summary
  const [loading, setLoading]                 = useState(false)
  const [toast, setToast]                     = useState(null)
  const [search, setSearch]                   = useState('')
  const [stageFilter, setStageFilter]         = useState('all')
  const [showModal, setShowModal]             = useState(false)
  const [editPR, setEditPR]                   = useState(null)
  const [viewPR, setViewPR]                   = useState(null)
  const [printPR, setPrintPR]                 = useState(null) // pr id to print
  const [viewLines, setViewLines]             = useState([])
  const [viewQtyCheck, setViewQtyCheck]       = useState({}) // sub_code → {budget_qty, committed_qty}
  const [eqViewQtyCheck, setEqViewQtyCheck]   = useState({})
  const [lines, setLines]                     = useState([{ ...EMPTY_LINE }])
  const [confirmDelete, setConfirmDelete]     = useState(null)
  const [rejectTarget, setRejectTarget]       = useState(null)
  const [rejectReason, setRejectReason]       = useState('')
  const [uploadLoading, setUploadLoading]     = useState(false)
  const fileInputRef                          = useRef(null)

  // Real authorization source -- can() mirrors has_permission() exactly,
  // same hook pattern as IPC/PO/Rental Equipment.
  const { can } = useProjectAuth(selectedProject?.id)
  // Real authenticated user's display name for audit fields
  // (cc_approved_by/proc_approved_by), replacing the removed temporary
  // Role Selector's ROLE_LABELS[tempRole] placeholder text.
  const { profile } = useAuth()
  const currentUserName = profile?.full_name || profile?.email || 'Unknown User'

  // ── Equipment PR state (separate table: equipment_prs) ──────────────
  const [eqPrs,          setEqPrs]          = useState([])
  const [eqCostCodes,    setEqCostCodes]    = useState([])
  const [eqSubCodes,     setEqSubCodes]     = useState([])
  const [eqLines,        setEqLines]        = useState({}) // { pr_id: [line,...] }
  const [eqLoading,      setEqLoading]      = useState(false)
  const [eqSearch,       setEqSearch]       = useState('')
  const [eqStageFilter,  setEqStageFilter]  = useState('all')
  const [eqShowModal,    setEqShowModal]    = useState(false)
  const [eqEditPR,       setEqEditPR]       = useState(null)
  const [eqViewPR,       setEqViewPR]       = useState(null)
  const [eqViewLines,    setEqViewLines]    = useState([])
  const [eqPrintPR,      setEqPrintPR]      = useState(null)
  const [eqLinesForm,    setEqLinesForm]    = useState([{ equipment_type:'', description:'', unit:'Hours', est_qty:'', cost_code_id:'', sub_code:'' }])
  const [eqConfirmDelete,setEqConfirmDelete]= useState(null)
  const [eqRejectTarget, setEqRejectTarget] = useState(null)
  const [eqRejectReason, setEqRejectReason] = useState('')
  const [eqForm, setEqForm] = useState({
    description: '', department: '', requested_by_name: '',
    required_by: '', cost_code_id: '', priority: 'normal', notes: ''
  })

  const [form, setForm] = useState({
    description: '', department: '', requested_by_name: '',
    required_by: '', cost_code_id: '', priority: 'normal', notes: ''
  })

  const showToast = (message, type = 'success') => setToast({ message, type })

  // ── Load projects & cost codes ────────────────────────────
  useEffect(() => {
    supabase.from('projects').select('id, name').order('name').then(({ data }) => setProjects(data || []))
  }, [])

  useEffect(() => {
    if (initProject) setSelectedProject(initProject)
  }, [initProject])

  useEffect(() => {
    if (selectedProject?.id) {
      loadPRs(selectedProject.id)
      loadCostCodes(selectedProject.id)
      loadBudgetSubCodes(selectedProject.id)
      loadEqPRs(selectedProject.id)
    }
  }, [selectedProject?.id]) // eslint-disable-line

  const loadCostCodes = async (projectId) => {
    const { data } = await supabase.from('cost_codes').select('id, code, description, level, parent_id')
      .eq('project_id', projectId).order('code')
    setCostCodes(data || [])
  }

  // Load Sub Codes (level=2) from cost_codes, with resolved Cost Code + Resource Code ancestry
  const loadBudgetSubCodes = async (projectId) => {
    const { data } = await supabase
      .from('cost_codes')
      .select('id, code, description, parent_id, level')
      .eq('project_id', projectId)
      .order('code')
    const all = data || []
    const byId = {}
    all.forEach(c => { byId[c.id] = c })
    const subs = all.filter(c => parseInt(c.level) === 2).map(sub => {
      const parent = byId[sub.parent_id]
      let cc = null, rc = null
      if (parent && parseInt(parent.level) === 4) {
        rc = parent
        cc = byId[parent.parent_id] || null
      } else if (parent && parseInt(parent.level) === 1) {
        cc = parent
      }
      return {
        ...sub,
        costCodeId: cc?.id || null,
        costCode: cc?.code || null,
        costCodeDescription: cc?.description || null,
        resourceCode: rc?.code || null,
        resourceCodeDescription: rc?.description || null,
      }
    })
    setBudgetSubCodes(subs)
  }

  const loadPRs = useCallback(async (projectId) => {
    setLoading(true)
    const { data, error } = await supabase
      .from('purchase_requisitions')
      .select('*, pr_line_items(*)')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
    if (error) showToast('فشل التحميل', 'error')
    else setPrs(data || [])
    setLoading(false)
  }, [])

  // ── Equipment PR loading (separate table) ────────────────
  const loadEqPRs = useCallback(async (projectId) => {
    setEqLoading(true)
    const [{ data: prData, error: prErr }, { data: ccData }] = await Promise.all([
      supabase.from('equipment_prs').select('*, cost_codes(code,description), equipment_pr_line_items(*)').eq('project_id', projectId).order('created_at', { ascending: false }),
      supabase.from('cost_codes').select('id, code, description, level, parent_id').eq('project_id', projectId).order('code'),
    ])
    if (prErr) { showToast('فشل تحميل Equipment PR', 'error'); setEqLoading(false); return }
    setEqPrs(prData || [])
    setEqCostCodes((ccData || []).filter(c => parseInt(c.level) === 1))
    const eqById = {}
    ;(ccData || []).forEach(c => { eqById[c.id] = c })
    setEqSubCodes((ccData || []).filter(c => parseInt(c.level) === 2).map(sub => {
      const parent = eqById[sub.parent_id]
      let cc = null, rc = null
      if (parent && parseInt(parent.level) === 4) {
        rc = parent
        cc = eqById[parent.parent_id] || null
      } else if (parent && parseInt(parent.level) === 1) {
        cc = parent
      }
      return {
        ...sub,
        costCodeId: cc?.id || null,
        costCode: cc?.code || null,
        costCodeDescription: cc?.description || null,
        resourceCode: rc?.code || null,
        resourceCodeDescription: rc?.description || null,
      }
    }))
    const grouped = {}
    ;(prData || []).forEach(p => { grouped[p.id] = p.equipment_pr_line_items || [] })
    setEqLines(grouped)
    setEqLoading(false)
  }, [])

  // ── Qty Check: budget vs committed (POs) per sub_code ────
  // Budget vs requested qty comparison (Material PR) — now via the shared
  // Hierarchy-based Direct/Indirect Budget Comparison (Material PR),
  // using the shared resolveHierarchyKey() -- Cost Code hierarchy is
  // the sole classification source, per the approved business rule.
  // Does NOT depend on purchase_orders.cost_type anywhere. Direct
  // Budget Qty unchanged from the prior Direct-only implementation
  // (tender_direct_items.resource_code, SUM(qty)). Indirect Budget Qty
  // is new: tender_indirect_items.cost_code, SUM(total_months) across
  // all detail rows (is_package_header=false, cost_code IS NOT NULL)
  // matching that Cost Code -- package-header rows are excluded since
  // their cost_code is always NULL and cannot be matched to any Sub
  // Code. total_months is intentionally read as-is, including 0 --
  // this is expected current data state per the approved decision, not
  // a bug; the comparison will reflect corrected values automatically
  // once Tender data is populated in a separate future step.
  const loadQtyCheck = async (prLines) => {
    const subCodes = [...new Set(prLines.map(l => l.sub_code).filter(Boolean))]
    if (!subCodes.length || !selectedProject?.id) return

    const [{ data: costCodesTree }, { data: tenderRows }, { data: indirectRows }, { data: poLines }] = await Promise.all([
      supabase.from('cost_codes').select('id, code, level, parent_id').eq('project_id', selectedProject.id),
      supabase.from('tender_direct_items')
        .select('resource_code, row_type, qty')
        .eq('project_id', selectedProject.id)
        .in('row_type', ['material', 'labour', 'subcontract', 'equipment']),
      supabase.from('tender_indirect_items')
        .select('cost_code, total_months')
        .eq('project_id', selectedProject.id)
        .eq('is_package_header', false),
      supabase.from('po_line_items')
        .select('sub_code, qty, purchase_orders!inner(project_id, status)')
        .eq('purchase_orders.project_id', selectedProject.id),
    ])
    const maps = buildCostCodeMaps(costCodesTree)
    const directBudgetQtyMap = {}
    ;(tenderRows || []).forEach(r => {
      const k = normSC(r.resource_code)
      if (!k) return
      directBudgetQtyMap[k] = (directBudgetQtyMap[k] || 0) + (parseFloat(r.qty) || 0)
    })
    const indirectBudgetQtyMap = {}
    ;(indirectRows || []).forEach(r => {
      const k = normSC(r.cost_code)
      if (!k) return
      indirectBudgetQtyMap[k] = (indirectBudgetQtyMap[k] || 0) + (parseFloat(r.total_months) || 0)
    })

    const check = {}
    subCodes.forEach(sc => {
      const r = resolveHierarchyKey(sc, maps)
      if (r.kind === 'unknown') { check[normSC(sc)] = { kind: 'unknown', label: r.label }; return }
      const budgetQty = r.kind === 'direct' ? (directBudgetQtyMap[r.key] || 0) : (indirectBudgetQtyMap[r.key] || 0)
      // Committed Qty is matched at the exact Sub Code level, NOT by
      // the resolved kind+key (Resource Code / Cost Code). The Budget
      // key can legitimately be shared by multiple distinct Sub Codes
      // (e.g. crane.500 and bobcat-skidloader both roll up to the same
      // Indirect Cost Code 41.00611) -- matching commitment by key
      // alone silently aggregates unrelated equipment/material lines
      // into one number. This applies identically to both Direct and
      // Indirect: Budget is classified/aggregated at the hierarchy
      // level, but Commitment is always counted at the PR's own
      // selected Sub Code, confirmed by direct inspection of real PO
      // data (crane.500 had zero actual POs, yet the old key-based
      // match returned 157,671.5 by summing every other equipment Sub
      // Code sharing the same parent Cost Code).
      const committedQty = (poLines || [])
        .filter(l => normSC(l.sub_code) === normSC(sc) && ['approved', 'closed'].includes(l.purchase_orders?.status))
        .reduce((s, l) => s + (parseFloat(l.qty) || 0), 0)
      check[normSC(sc)] = { kind: r.kind, label: r.label, budget_qty: budgetQty, committed_qty: committedQty, remaining: budgetQty - committedQty }
    })
    setViewQtyCheck(check)

  }

  // ── Equipment Qty Check: budget vs committed (Equipment POs) per sub_code ────
  // Budget vs requested qty comparison (equipment version) — now via the
  // shared Resource Code SSOT (resolveDirectKey/buildCostCodeMaps from
  // budgetVsCommitment.js), replacing the legacy budget_equipment_code
  // column (confirmed 0% populated). Mirrors PurchaseOrders.jsx's Phase
  // PO-3 Direct resolution, not a separate Equipment-specific mapping.
  // Hierarchy-based Direct/Indirect Budget Comparison (Equipment PR),
  // mirroring loadQtyCheck() (Material) exactly -- same
  // resolveHierarchyKey(), same collision guard (kind+key), same
  // total_months-as-is treatment for Indirect. Commitment source is
  // rental_equipment_po_line_items.quantity from approved/closed Rental
  // Equipment POs only, per the approved business rule.
  const eqLoadQtyCheck = async (prLines) => {
    const subCodes = [...new Set(prLines.map(l => l.sub_code || l.equipment_type).filter(Boolean))]
    if (!subCodes.length || !selectedProject?.id) return

    const [{ data: costCodesTree }, { data: tenderRows }, { data: indirectRows }, { data: eqPoLines }] = await Promise.all([
      supabase.from('cost_codes').select('id, code, level, parent_id').eq('project_id', selectedProject.id),
      supabase.from('tender_direct_items')
        .select('resource_code, row_type, qty')
        .eq('project_id', selectedProject.id)
        .eq('row_type', 'equipment'),
      supabase.from('tender_indirect_items')
        .select('cost_code, total_months')
        .eq('project_id', selectedProject.id)
        .eq('is_package_header', false),
      supabase.from('rental_equipment_po_line_items')
        .select('equipment_type, quantity, rental_equipment_pos!inner(project_id, status)')
        .eq('rental_equipment_pos.project_id', selectedProject.id),
    ])
    const maps = buildCostCodeMaps(costCodesTree)
    const directBudgetQtyMap = {}
    ;(tenderRows || []).forEach(r => {
      const k = normSC(r.resource_code)
      if (!k) return
      directBudgetQtyMap[k] = (directBudgetQtyMap[k] || 0) + (parseFloat(r.qty) || 0)
    })
    const indirectBudgetQtyMap = {}
    ;(indirectRows || []).forEach(r => {
      const k = normSC(r.cost_code)
      if (!k) return
      indirectBudgetQtyMap[k] = (indirectBudgetQtyMap[k] || 0) + (parseFloat(r.total_months) || 0)
    })

    const check = {}
    subCodes.forEach(sc => {
      const r = resolveHierarchyKey(sc, maps)
      if (r.kind === 'unknown') { check[normSC(sc)] = { kind: 'unknown', label: r.label }; return }
      const budgetQty = r.kind === 'direct' ? (directBudgetQtyMap[r.key] || 0) : (indirectBudgetQtyMap[r.key] || 0)
      // Same Sub-Code-level commitment matching as loadQtyCheck()
      // (Material) -- see that function's comment for the full
      // rationale. Budget key can be shared across multiple distinct
      // equipment Sub Codes; Commitment must always be counted only
      // for the PR's own selected Sub Code (equipment_type).
      const committedQty = (eqPoLines || [])
        .filter(l => normSC(l.equipment_type) === normSC(sc) && ['approved', 'closed'].includes(l.rental_equipment_pos?.status))
        .reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0)
      check[normSC(sc)] = { kind: r.kind, label: r.label, budget_qty: budgetQty, committed_qty: committedQty, remaining: budgetQty - committedQty }
    })
    setEqViewQtyCheck(check)
  }

  // ── Open modals ───────────────────────────────────────────
  const openNew = () => {
    setEditPR(null)
    setForm({ description: '', department: '', requested_by_name: '', required_by: '', cost_code_id: '', priority: 'normal', notes: '' })
    setLines([{ ...EMPTY_LINE }])
    setShowModal(true)
  }

  const openEdit = async (pr) => {
    // Defense-in-depth: same check as the button's visibility condition,
    // in case this function is ever called from another path.
    if (!can('PR.Edit')) { showToast('التعديل متاح فقط لدور Site Engineer', 'error'); return }
    setEditPR(pr)
    setForm({
      description: pr.description || '',
      requested_by_name: pr.requested_by_name || '',
      department: pr.department || '',
      required_by: pr.required_by || '',
      cost_code_id: pr.cost_code_id || '',
      priority: pr.priority || 'normal',
      notes: pr.notes || ''
    })
    const { data } = await supabase.from('pr_line_items').select('*').eq('pr_id', pr.id).order('created_at')
    setLines(data?.length ? data : [{ ...EMPTY_LINE }])
    setShowModal(true)
  }

  const openView = async (pr) => {
    setViewPR(pr)
    const { data } = await supabase.from('pr_line_items').select('*').eq('pr_id', pr.id).order('created_at')
    const ls = data || []
    setViewLines(ls)
    loadQtyCheck(ls)
  }

  // ── Save PR ───────────────────────────────────────────────
  const [prSaving, setPrSaving] = useState(false)
  const handleSave = async () => {
    if (prSaving) return
    if (!form.description.trim()) return showToast('الوصف مطلوب', 'error')
    if (!selectedProject?.id) return showToast('اختر مشروع', 'error')
    setPrSaving(true)
    try {

    let prNumber = editPR?.pr_number
    if (!prNumber) {
      const { data: cnt } = await supabase.from('purchase_requisitions').select('id').eq('project_id', selectedProject.id)
      prNumber = `PR-${new Date().getFullYear()}-${String((cnt?.length || 0) + 1).padStart(4, '0')}`
    }

    const firstLineSub = lines.find(l => l.sub_code)?.sub_code
    const derivedCostCodeId = firstLineSub ? (budgetSubCodes.find(s => s.code === firstLineSub)?.costCodeId || null) : null

    const payload = {
      project_id:         selectedProject.id,
      pr_number:          prNumber,
      description:        form.description,
      requested_by_name:  form.requested_by_name || null,
      department:         form.department || null,
      required_by:        form.required_by || null,
      cost_code_id:       derivedCostCodeId,
      priority:           form.priority || 'normal',
      notes:              form.notes || null,
      ...(editPR ? {} : { status: 'draft', approval_stage: 'draft' }),
    }

    let prId
    if (editPR) {
      const { pr_number: _, project_id: __, ...updatePayload } = payload
      const { error } = await supabase.from('purchase_requisitions').update(updatePayload).eq('id', editPR.id)
      if (error) return showToast('فشل الحفظ: ' + error.message, 'error')
      prId = editPR.id
    } else {
      const { data, error } = await supabase.from('purchase_requisitions').insert(payload).select().single()
      if (error) return showToast('فشل الحفظ: ' + error.message, 'error')
      prId = data.id
    }

    // Save lines (no est_rate)
    const validLines = lines.filter(l => l.description?.trim())
    await supabase.from('pr_line_items').delete().eq('pr_id', prId)
    if (validLines.length > 0) {
      await supabase.from('pr_line_items').insert(
        validLines.map(l => ({
          pr_id:       prId,
          description: l.description,
          unit:        l.unit || null,
          est_qty:     parseFloat(l.est_qty) || null,
          sub_code:    l.sub_code || null,
          notes:       l.notes || null,
        }))
      )
    }

    showToast(editPR ? 'تم التحديث' : 'تم الإنشاء')
    setShowModal(false)
    loadPRs(selectedProject.id)
    } finally {
      setPrSaving(false)
    }
  }

  // ── Approval Actions ──────────────────────────────────────
  const handleApprovalAction = async (pr, action, reason = '') => {
    const stage = pr.approval_stage || 'draft'
    let update = {}

    if (action === 'submit') {
      update = { approval_stage: 'cost_control', status: 'pending', rejection_reason: null }
    } else if (action === 'approve') {
      if (stage === 'cost_control') {
        update = { approval_stage: 'procurement', cc_approved_by: currentUserName, cc_approved_at: new Date().toISOString() }
      } else if (stage === 'procurement') {
        update = { approval_stage: 'approved', status: 'approved', proc_approved_by: currentUserName, proc_approved_at: new Date().toISOString() }
      }
    } else if (action === 'reject') {
      update = { approval_stage: 'rejected', status: 'rejected', rejection_reason: reason }
    }

    if (!Object.keys(update).length) return
    const { error } = await supabase.from('purchase_requisitions').update(update).eq('id', pr.id)
    if (error) return showToast('فشل: ' + error.message, 'error')
    showToast('تم التحديث')
    if (viewPR?.id === pr.id) setViewPR(p => ({ ...p, ...update }))
    loadPRs(selectedProject.id)
  }

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (id) => {
    // Defense-in-depth: same reasoning as openEdit() above.
    if (!can('PR.Delete')) { showToast('الحذف متاح فقط لدور Site Engineer', 'error'); return }
    // Unlink any RFQs referencing this PR
    await supabase.from('rfq_requests').update({ pr_id: null }).eq('pr_id', id)
    await supabase.from('pr_line_items').delete().eq('pr_id', id)
    const { error } = await supabase.from('purchase_requisitions').delete().eq('id', id)
    if (error) return showToast('فشل الحذف: ' + error.message, 'error')
    showToast('تم الحذف')
    setConfirmDelete(null)
    loadPRs(selectedProject.id)
  }

  // ═══════════════════ Equipment PR — separate flow ═══════════════════
  function eqOpenNew() {
    setEqForm({ description:'', department:'', requested_by_name:'', required_by:'', cost_code_id:'', priority:'normal', notes:'' })
    setEqLinesForm([{ equipment_type:'', description:'', unit:'Hours', est_qty:'', cost_code_id:'', sub_code:'' }])
    setEqEditPR(null)
    setEqShowModal(true)
  }
  function eqOpenEdit(pr) {
    // Defense-in-depth: same reasoning as openEdit() (Material) above.
    if (!can('EquipmentPR.Edit')) { showToast('التعديل متاح فقط لدور Site Engineer', 'error'); return }
    setEqForm({
      description: pr.description||'', department: pr.department||'', requested_by_name: pr.requested_by_name||'',
      required_by: pr.required_by||'', cost_code_id: pr.cost_code_id||'', priority: pr.priority||'normal', notes: pr.notes||'',
    })
    // Reconstruct sub_code_id from the stored equipment_type/sub_code --
    // equipment_pr_line_items has no sub_code_id column at all, and the
    // Edit <select> below relies on sub_code_id, so without this it
    // always starts blank on reopen regardless of what was saved.
    // normSC() matches case-insensitively since equipment_type/sub_code
    // is normalized to lowercase in the database by an existing trigger.
    setEqLinesForm((eqLines[pr.id]||[]).map(l => {
      const sc = eqSubCodes.find(s => normSC(s.code) === normSC(l.equipment_type || l.sub_code))
      return { ...l, sub_code_id: sc?.id || '' }
    }).length ? (eqLines[pr.id]||[]).map(l => {
      const sc = eqSubCodes.find(s => normSC(s.code) === normSC(l.equipment_type || l.sub_code))
      return { ...l, sub_code_id: sc?.id || '' }
    }) : [{ equipment_type:'', description:'', unit:'Hours', est_qty:'', cost_code_id:'', sub_code:'' }])
    setEqEditPR(pr)
    setEqShowModal(true)
  }
  function eqAddLine() { setEqLinesForm(l => [...l, { equipment_type:'', description:'', unit:'Hours', est_qty:'', cost_code_id:'', sub_code:'' }]) }
  function eqRemoveLine(idx) { setEqLinesForm(l => l.filter((_,i) => i!==idx)) }
  function eqHLine(idx, k, v) { setEqLinesForm(l => l.map((line,i) => i===idx ? { ...line, [k]: v } : line)) }

  const [eqPrSaving, setEqPrSaving] = useState(false)
  async function eqHandleSave() {
    if (eqPrSaving) return
    if (!eqForm.description.trim()) return showToast('الوصف مطلوب', 'error')
    if (!selectedProject?.id) return showToast('اختر مشروع', 'error')
    setEqPrSaving(true)
    try {

    let prNumber = eqEditPR?.pr_number
    if (!prNumber) {
      const { data: cnt } = await supabase.from('equipment_prs').select('id').eq('project_id', selectedProject.id)
      prNumber = `EPR-${new Date().getFullYear()}-${String((cnt?.length || 0) + 1).padStart(4, '0')}`
    }

    const payload = {
      project_id: selectedProject.id, pr_number: prNumber, description: eqForm.description,
      requested_by_name: eqForm.requested_by_name||null, department: eqForm.department||null,
      required_by: eqForm.required_by||null, cost_code_id: eqForm.cost_code_id||null,
      priority: eqForm.priority||'normal', notes: eqForm.notes||null,
      ...(eqEditPR ? {} : { status:'draft', approval_stage:'draft' }),
    }

    let prId
    if (eqEditPR) {
      const { pr_number:_, project_id:__, ...updatePayload } = payload
      const { error } = await supabase.from('equipment_prs').update(updatePayload).eq('id', eqEditPR.id)
      if (error) return showToast('فشل الحفظ: '+error.message, 'error')
      prId = eqEditPR.id
      await supabase.from('equipment_pr_line_items').delete().eq('pr_id', prId)
    } else {
      const { data, error } = await supabase.from('equipment_prs').insert(payload).select().single()
      if (error) return showToast('فشل الحفظ: '+error.message, 'error')
      prId = data.id
    }

    const validLines = eqLinesForm.filter(l => l.equipment_type)
    if (validLines.length) {
      const { error: linesErr } = await supabase.from('equipment_pr_line_items').insert(
        validLines.map(l => ({
          pr_id: prId, equipment_type: l.equipment_type, description: l.description||null,
          unit: l.unit||'Hours', est_qty: parseFloat(l.est_qty)||null,
          cost_code_id: l.cost_code_id||null, sub_code: l.sub_code||null,
        }))
      )
      if (linesErr) return showToast('فشل حفظ البنود: '+linesErr.message, 'error')
    }

    showToast(eqEditPR ? 'تم التحديث' : 'تم الإنشاء')
    setEqShowModal(false)
    loadEqPRs(selectedProject.id)
    } finally {
      setEqPrSaving(false)
    }
  }

  async function eqHandleApprovalAction(pr, action, reason='') {
    const stage = pr.approval_stage || 'draft'
    let update = {}
    if (action === 'submit') {
      update = { approval_stage:'cost_control', status:'pending', rejection_reason:null }
    } else if (action === 'approve') {
      if (stage === 'cost_control') {
        update = { approval_stage:'procurement', cc_approved_by: currentUserName, cc_approved_at: new Date().toISOString() }
      } else if (stage === 'procurement') {
        update = { approval_stage:'approved', status:'approved', proc_approved_by: currentUserName, proc_approved_at: new Date().toISOString() }
      }
    } else if (action === 'reject') {
      update = { approval_stage:'rejected', status:'rejected', rejection_reason: reason }
    }
    if (!Object.keys(update).length) return
    const { error } = await supabase.from('equipment_prs').update(update).eq('id', pr.id)
    if (error) return showToast('فشل: '+error.message, 'error')
    showToast('تم التحديث')
    if (eqViewPR?.id === pr.id) setEqViewPR(p => ({ ...p, ...update }))
    loadEqPRs(selectedProject.id)
  }

  async function eqHandleDelete(id) {
    // Defense-in-depth: same reasoning as handleDelete() (Material) above.
    if (!can('EquipmentPR.Delete')) { showToast('الحذف متاح فقط لدور Site Engineer', 'error'); return }
    await supabase.from('equipment_rfqs').update({ pr_id: null }).eq('pr_id', id)
    await supabase.from('equipment_pr_line_items').delete().eq('pr_id', id)
    const { error } = await supabase.from('equipment_prs').delete().eq('id', id)
    if (error) return showToast('فشل الحذف: '+error.message, 'error')
    showToast('تم الحذف')
    setEqConfirmDelete(null)
    loadEqPRs(selectedProject.id)
  }

  // Main-list Cost Code display: line-derived first (Sub Code -> Cost
  // Code), falling back to the legacy equipment_prs.cost_code_id Header
  // join ONLY when no line resolves to a valid Cost Code at all -- this
  // preserves old PRs created before line-level Sub Codes were
  // meaningful (e.g. equipment_type free-text values with no matching
  // cost_codes row) without ever writing/migrating cost_code_id itself.
  // Multiple distinct line-level Cost Codes still win over the fallback
  // (never silently picked as if it were a single answer) -- the
  // fallback only applies when the line-derived set is EMPTY, never
  // when it is ambiguous.
  function eqDeriveCostCodeDisplay(pr) {
    const lines = eqLines[pr.id] || []
    const codes = new Set(
      lines
        .map(l => eqSubCodes.find(s => normSC(s.code) === normSC(l.equipment_type || l.sub_code))?.costCode)
        .filter(Boolean)
    )
    if (codes.size === 1) return [...codes][0]
    if (codes.size > 1) return 'Multiple'
    // codes.size === 0: no line resolves to a Cost Code at all.
    return pr.cost_codes?.code || '—'
  }

  function eqGetApprovalButtons(pr) {
    const stage = pr.approval_stage || 'draft'
    const btns = []
    if (stage === 'draft' && can('EquipmentPR.Submit')) btns.push({ label:'Submit', action:'submit', color:'bg-blue-600 text-white' })
    if (stage === 'cost_control' && can('EquipmentPR.ApproveCostControl')) {
      btns.push({ label:'✓ Approve', action:'approve', color:'bg-green-600 text-white' })
      btns.push({ label:'✗ Reject', action:'reject', color:'bg-red-100 text-red-700' })
    }
    if (stage === 'procurement' && can('EquipmentPR.ApproveProcurement')) {
      btns.push({ label:'✓ Approve', action:'approve', color:'bg-green-600 text-white' })
      btns.push({ label:'✗ Reject', action:'reject', color:'bg-red-100 text-red-700' })
    }
    return btns
  }

  function eqDownloadTemplate() {
    const ws = XLSX.utils.aoa_to_sheet([
      ['description','department','requested_by_name','required_by','cost_code','priority','notes','line_sub_code','line_est_qty'],
      ['Excavator needed for site works','Site','Ahmed Ali','2026-08-01','41.006110','normal','','EXC-20T',200],
    ])
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Equipment PR'); XLSX.writeFile(wb, 'equipment_pr_template.xlsx')
  }

  const [eqUploadLoading, setEqUploadLoading] = useState(false)
  const eqFileInputRef = useRef(null)

  const handleEqUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !selectedProject?.id) return
    setEqUploadLoading(true)
    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf)
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]])
      const groups = {}
      rows.forEach(r => {
        const key = String(r.description || '').trim()
        if (!key) return
        if (!groups[key]) groups[key] = { meta: r, lines: [] }
        if (r.line_sub_code) groups[key].lines.push({ sub_code: String(r.line_sub_code).trim(), est_qty: parseFloat(r.line_est_qty) || null })
      })
      let created = 0
      for (const [desc, { meta, lines: prLines }] of Object.entries(groups)) {
        const { data: cnt } = await supabase.from('equipment_prs').select('id').eq('project_id', selectedProject.id)
        const prNumber = `EPR-${new Date().getFullYear()}-${String((cnt?.length || 0) + 1).padStart(4, '0')}`
        const ccMatch = eqCostCodes.find(cc => cc.code === String(meta.cost_code || '').trim())
        const { data: pr, error } = await supabase.from('equipment_prs').insert({
          project_id: selectedProject.id, pr_number: prNumber, description: desc,
          department: meta.department || null, requested_by_name: meta.requested_by_name || null,
          required_by: meta.required_by || null, cost_code_id: ccMatch?.id || null,
          priority: meta.priority || 'normal', notes: meta.notes || null,
          status: 'draft', approval_stage: 'draft',
        }).select().single()
        if (error) continue
        const lineRows = prLines.map(l => {
          const sc = eqSubCodes.find(s => s.code === l.sub_code)
          return {
            pr_id: pr.id, equipment_type: sc?.code || l.sub_code, description: sc?.description || null,
            unit: 'Hours', est_qty: l.est_qty, cost_code_id: sc?.costCodeId || null, sub_code: sc?.code || l.sub_code,
          }
        })
        if (lineRows.length > 0) await supabase.from('equipment_pr_line_items').insert(lineRows)
        created++
      }
      showToast(`تم رفع ${created} Equipment PR`)
      loadEqPRs(selectedProject.id)
    } catch(err) { showToast('خطأ: ' + err.message, 'error') }
    setEqUploadLoading(false)
    e.target.value = ''
  }

  const eqFilteredPRs = eqPrs.filter(pr => {
    if (eqStageFilter !== 'all' && (pr.approval_stage||'draft') !== eqStageFilter) return false
    if (eqSearch && !(pr.pr_number?.toLowerCase().includes(eqSearch.toLowerCase()) || pr.description?.toLowerCase().includes(eqSearch.toLowerCase()))) return false
    return true
  })

  const eqStageCounts = {
    all: eqPrs.length,
    draft: eqPrs.filter(p=>(p.approval_stage||'draft')==='draft').length,
    cost_control: eqPrs.filter(p=>p.approval_stage==='cost_control').length,
    procurement: eqPrs.filter(p=>p.approval_stage==='procurement').length,
    approved: eqPrs.filter(p=>p.approval_stage==='approved').length,
    rejected: eqPrs.filter(p=>p.approval_stage==='rejected').length,
    converted: eqPrs.filter(p=>p.approval_stage==='converted').length,
  }

  // ── Upload Excel ──────────────────────────────────────────
  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['description', 'department', 'requested_by_name', 'required_by', 'cost_code', 'priority', 'notes', 'line_description', 'line_unit', 'line_qty', 'line_sub_code'],
      ['Steel procurement', 'Site', 'Ahmed Ali', '2026-07-15', '03 10 00', 'normal', '', 'Steel Rebar 12mm', 'MT', '50', 'MAT-1.1'],
    ])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'PR Template')
    XLSX.writeFile(wb, 'PR_Upload_Template.xlsx')
  }

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !selectedProject?.id) return
    setUploadLoading(true)
    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf)
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]])
      const groups = {}
      rows.forEach(r => {
        const key = String(r.description || '').trim()
        if (!key) return
        if (!groups[key]) groups[key] = { meta: r, lines: [] }
        if (r.line_description) groups[key].lines.push({
          description: String(r.line_description),
          unit:        r.line_unit || null,
          est_qty:     parseFloat(r.line_qty) || null,
          sub_code:    r.line_sub_code || null,
        })
      })
      let created = 0
      for (const [desc, { meta, lines: prLines }] of Object.entries(groups)) {
        const { data: cnt } = await supabase.from('purchase_requisitions').select('id').eq('project_id', selectedProject.id)
        const prNumber = `PR-${new Date().getFullYear()}-${String((cnt?.length || 0) + 1).padStart(4, '0')}`
        const ccMatch = costCodes.find(cc => cc.code === String(meta.cost_code || '').trim())
        const { data: pr, error } = await supabase.from('purchase_requisitions').insert({
          project_id: selectedProject.id, pr_number: prNumber, description: desc,
          department: meta.department || null, required_by: meta.required_by || null,
          cost_code_id: ccMatch?.id || null, priority: meta.priority || 'normal',
          notes: meta.notes || null, status: 'draft', approval_stage: 'draft',
        }).select().single()
        if (error) continue
        if (prLines.length > 0) {
          await supabase.from('pr_line_items').insert(prLines.map(l => ({ pr_id: pr.id, ...l })))
        }
        created++
      }
      showToast(`تم رفع ${created} PR`)
      loadPRs(selectedProject.id)
    } catch(err) { showToast('خطأ: ' + err.message, 'error') }
    setUploadLoading(false)
    e.target.value = ''
  }

  // ── Approval buttons logic ────────────────────────────────
  const getApprovalButtons = (pr) => {
    const stage = pr.approval_stage || 'draft'
    const buttons = []

    // Site: Submit (draft) or Resubmit (rejected)
    if ((stage === 'draft' || stage === 'rejected') && can('PR.Submit')) {
      buttons.push({ label: stage === 'rejected' ? '↺ Resubmit' : 'Submit', action: 'submit', color: 'bg-blue-600 text-white' })
    }
    // Cost Control: Approve or Reject (when stage = cost_control)
    if (stage === 'cost_control' && can('PR.ApproveCostControl')) {
      buttons.push({ label: '✓ Approve', action: 'approve', color: 'bg-green-600 text-white' })
      buttons.push({ label: '✗ Reject', action: 'reject', color: 'bg-red-100 text-red-700' })
    }
    // Procurement: Final Approve or Reject
    if (stage === 'procurement' && can('PR.ApproveProcurement')) {
      buttons.push({ label: '✓ Final Approve', action: 'approve', color: 'bg-green-600 text-white' })
      buttons.push({ label: '✗ Reject', action: 'reject', color: 'bg-red-100 text-red-700' })
    }
    return buttons
  }

  // ── Filter ────────────────────────────────────────────────
  const filtered = prs.filter(pr => {
    const matchSearch = !search ||
      pr.description?.toLowerCase().includes(search.toLowerCase()) ||
      pr.pr_number?.toLowerCase().includes(search.toLowerCase())
    const matchStage = stageFilter === 'all' || (pr.approval_stage || 'draft') === stageFilter
    return matchSearch && matchStage
  })

  const parentCostCodes = costCodes.filter(c => parseInt(c.level) === 1)
  const selectedCC = costCodes.find(c => c.id === form.cost_code_id)

  return (
    <div className="min-h-screen bg-[#F3F4F6] font-[inherit]">
      {toast && <Toast {...toast} onClose={() => setToast(null)}/>}
      {confirmDelete && (
        <ConfirmOverlay
          message={`حذف PR "${confirmDelete.pr_number}"؟`}
          onConfirm={() => handleDelete(confirmDelete.id)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setRejectTarget(null)}/>
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-96">
            <h3 className="text-sm font-semibold text-[#1C1A18] mb-3">Rejection Reason</h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={3} placeholder="سبب الرفض..."
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none resize-none mb-4"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setRejectTarget(null)} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg">Cancel</button>
              <button
                onClick={() => { handleApprovalAction(rejectTarget, 'reject', rejectReason); setRejectTarget(null); setRejectReason('') }}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg"
              >Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-extrabold text-[#0F1017]">Purchase Requisitions</h1>
            <p className="text-xs text-[#6B7280] mt-0.5">إنشاء وإدارة طلبات الشراء</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedProject && activeTab === 'material' && (
              <>
                <button onClick={downloadTemplate} className="px-3 py-2 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] text-[#6B7280]">
                  ↓ Template
                </button>
                {can('PR.Create') && (
                  <label className="px-3 py-2 text-xs border border-[#1B3A6B] text-[#1B3A6B] rounded-lg hover:bg-[#1B3A6B]/5 cursor-pointer">
                    {uploadLoading ? 'Uploading…' : '↑ Upload'}
                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden"/>
                  </label>
                )}
                {can('PR.Create') && (
                  <button onClick={openNew} className="px-3 py-2 text-xs bg-[#1B3A6B] text-white rounded-lg hover:bg-[#1B3A6B]/90">
                    + New PR
                  </button>
                )}
              </>
            )}
            {selectedProject && activeTab === 'equipment' && (
              <>
                <button onClick={eqDownloadTemplate} className="px-3 py-2 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] text-[#6B7280]">
                  ↓ Template
                </button>
                {can('EquipmentPR.Create') && (
                  <label className="px-3 py-2 text-xs border border-orange-400 text-orange-600 rounded-lg hover:bg-orange-50 cursor-pointer">
                    {eqUploadLoading ? 'Uploading…' : '↑ Upload'}
                    <input ref={eqFileInputRef} type="file" accept=".xlsx,.xls" onChange={handleEqUpload} className="hidden"/>
                  </label>
                )}
                {can('EquipmentPR.Create') && (
                  <button onClick={eqOpenNew} className="px-3 py-2 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600">
                    + New Equipment PR
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        {/* Tab Bar */}
        <div className="flex gap-1 mt-3 border-b border-[#E5E7EB]">
          <button onClick={()=>setActiveTab('material')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab==='material' ? 'border-[#1B3A6B] text-[#1B3A6B]' : 'border-transparent text-[#6B7280] hover:text-[#0F1017]'}`}>
            📦 Material
          </button>
          <button onClick={()=>setActiveTab('equipment')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab==='equipment' ? 'border-orange-500 text-orange-600' : 'border-transparent text-[#6B7280] hover:text-[#0F1017]'}`}>
            🚜 Equipment
          </button>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Project selector */}
        {!selectedProject ? (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-8 text-center">
            <p className="text-sm text-[#6B7280] mb-4">اختر مشروع</p>
            <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto">
              {projects.map(p => (
                <button key={p.id} onClick={() => setSelectedProject(p)}
                  className="text-left p-4 bg-white border border-[#E5E7EB] rounded-xl hover:border-[#1B3A6B] transition-colors">
                  <div className="font-semibold text-[#1C1A18] text-sm">{p.name}</div>
                </button>
              ))}
            </div>
          </div>
        ) : activeTab === 'material' ? (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Total PRs',     value: prs.length,                                        color: 'text-[#1C1A18]' },
                { label: 'In Progress',   value: prs.filter(p => ['site','cost_control','procurement'].includes(p.approval_stage)).length, color: 'text-amber-600' },
                { label: 'Approved',      value: prs.filter(p => p.approval_stage === 'approved').length,  color: 'text-[#1A7A4A]' },
                { label: 'Converted',     value: prs.filter(p => p.approval_stage === 'converted').length, color: 'text-indigo-600' },
              ].map(k => (
                <div key={k.label} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                  <p className="text-xs text-[#9CA3AF] uppercase font-semibold mb-1">{k.label}</p>
                  <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search PR number or description..."
                className="flex-1 max-w-xs border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none bg-white"/>
              <div className="flex gap-1 flex-wrap">
                {['all', 'draft', 'site', 'cost_control', 'procurement', 'approved', 'rejected', 'converted'].map(s => (
                  <button key={s} onClick={() => setStageFilter(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                      ${stageFilter === s ? 'bg-[#1B3A6B] text-white' : 'bg-white border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F9FAFB]'}`}>
                    {s === 'all' ? 'All' : STAGE_CONFIG[s]?.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-[#9CA3AF] ml-auto">{filtered.length} PR</span>
            </div>

            {/* Table */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 border-2 border-[#1B3A6B]/40 border-t-[#1B3A6B] rounded-full animate-spin"/>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">PR #</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Description</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Requested By</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Priority</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Required</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Stage</th>
                      <th className="px-4 py-3 w-40"/>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {filtered.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-12 text-[#9CA3AF] text-sm">لا يوجد PRs</td></tr>
                    ) : filtered.map(pr => {
                      const approvalBtns = getApprovalButtons(pr)
                      return (
                        <tr key={pr.id} className="hover:bg-[#FAFAFA] group">
                          <td className="px-4 py-3 font-mono text-xs text-[#1B3A6B] font-bold">{pr.pr_number}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-[#1C1A18]">{pr.description}</div>
                            {pr.department && <div className="text-xs text-[#9CA3AF]">{pr.department}</div>}
                          </td>
                          <td className="px-4 py-3 text-[#6B7280] text-xs">{pr.requested_by_name || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-semibold ${PRIORITY_CONFIG[pr.priority]?.color}`}>
                              {PRIORITY_CONFIG[pr.priority]?.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#6B7280] text-xs">{pr.required_by || '—'}</td>
                          <td className="px-4 py-3"><StageBadge stage={pr.approval_stage || 'draft'}/></td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 justify-end">
                              <button onClick={() => openView(pr)}
                                className="px-2 py-1 text-xs text-[#6B7280] border border-[#E5E7EB] rounded hover:bg-[#F3F4F6]">View</button>
                              <button onClick={() => setPrintPR(pr.id)}
                                className="px-2 py-1 text-xs text-[#1B3A6B] border border-[#DBEAFE] bg-[#EFF6FF] rounded hover:bg-[#DBEAFE]">🖨 Print</button>
                              {/* Bugfix: Edit was gated only to approval_stage, with
                                  no role check at all -- any of the 5 roles able to
                                  open this page could edit a draft/rejected PR.
                                  Now restricted to site_engineer, the confirmed
                                  business owner of PR creation/submission (see
                                  the identical role check on "+ New PR" and
                                  "Submit/Resubmit" elsewhere in this file). */}
                              {can('PR.Edit') && ['draft', 'rejected'].includes(pr.approval_stage || 'draft') && (
                                <button onClick={() => openEdit(pr)}
                                  className="px-2 py-1 text-xs text-[#6B7280] border border-[#E5E7EB] rounded hover:bg-[#F3F4F6]">Edit</button>
                              )}
                              {approvalBtns.map(btn => (
                                <button key={btn.action} onClick={() => {
                                  if (btn.action === 'reject') { setRejectTarget(pr); setRejectReason('') }
                                  else handleApprovalAction(pr, btn.action)
                                }}
                                  className={`px-2 py-1 text-xs rounded border border-transparent ${btn.color}`}>
                                  {btn.label}
                                </button>
                              ))}
                              {/* Bugfix: Delete previously allowed for any stage
                                  except approved/converted (including cost_control
                                  and procurement -- PRs actively under review by
                                  another role), with no role check at all. Now
                                  restricted to draft-only (matching Equipment PR's
                                  existing behavior and the approved Phase A2
                                  policy) AND site_engineer only, matching Edit
                                  above. Server-side draft-only enforcement is
                                  provided by pr_delete_guard; role enforcement
                                  (site_engineer via auth.uid()) is deferred until
                                  this screen is migrated to real useAuth(). */}
                              {can('PR.Delete') && (pr.approval_stage || 'draft') === 'draft' && (
                                <button onClick={() => setConfirmDelete(pr)}
                                  className="px-2 py-1 text-xs text-red-600 border border-red-100 rounded hover:bg-red-50">🗑</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

      {/* ── View Modal ──────────────────────────────────────── */}
      {viewPR && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB] sticky top-0 bg-white z-10">
              <div>
                <div className="font-mono text-xs text-[#1B3A6B] font-bold">{viewPR.pr_number}</div>
                <h2 className="text-lg font-bold text-[#1C1A18]">{viewPR.description}</h2>
              </div>
              <div className="flex items-center gap-2">
                <StageBadge stage={viewPR.approval_stage || 'draft'}/>
                <button onClick={() => setPrintPR(viewPR.id)}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-[#1B3A6B] rounded-lg hover:bg-[#152d54]">🖨 Print</button>
                <button onClick={() => setViewPR(null)} className="text-[#9CA3AF] hover:text-[#1C1A18] text-xl">✕</button>
              </div>
            </div>
            <div className="p-5 space-y-5">
              {/* Meta */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                {[
                  ['Requested By', viewPR.requested_by_name],
                  ['Department',   viewPR.department],
                  ['Required Date',viewPR.required_by],
                  ['Priority',     PRIORITY_CONFIG[viewPR.priority]?.label],
                  ['Created',      new Date(viewPR.created_at).toLocaleDateString('en-GB')],
                  ['Notes',        viewPR.notes],
                ].map(([k, v]) => v ? (
                  <div key={k}>
                    <div className="text-xs text-[#9CA3AF] uppercase">{k}</div>
                    <div className="font-medium text-[#1C1A18] text-sm">{v}</div>
                  </div>
                ) : null)}
              </div>

              {/* Approval Trail */}
              <div className="bg-[#F9FAFB] rounded-xl p-4">
                <div className="text-xs font-semibold text-[#6B7280] uppercase mb-3">Approval Trail</div>
                <div className="flex items-center gap-2 flex-wrap">
                  {[
                    { key: 'cost_control', label: 'Cost Control', by: viewPR.cc_approved_by,   at: viewPR.cc_approved_at },
                    { key: 'procurement',  label: 'Procurement',  by: viewPR.proc_approved_by, at: viewPR.proc_approved_at },
                  ].map((step, i) => {
                    const stage = viewPR.approval_stage || 'draft'
                    const stages = ['draft', 'cost_control', 'procurement', 'approved', 'rejected', 'converted']
                    const stageIdx = stages.indexOf(stage)
                    const stepIdx  = stages.indexOf(step.key)
                    const done     = step.by || stageIdx > stepIdx
                    const current  = stage === step.key
                    return (
                      <div key={step.key} className="flex items-center gap-2">
                        {i > 0 && <div className="w-8 h-px bg-[#E5E7EB]"/>}
                        <div className={`flex flex-col items-center px-3 py-2 rounded-lg border text-xs
                          ${done ? 'bg-green-50 border-green-200' : current ? 'bg-amber-50 border-amber-200' : 'bg-white border-[#E5E7EB]'}`}>
                          <span className={`font-semibold ${done ? 'text-green-700' : current ? 'text-amber-700' : 'text-[#9CA3AF]'}`}>
                            {done ? '✓' : current ? '⏳' : '○'} {step.label}
                          </span>
                          {step.by && <span className="text-[10px] text-[#6B7280] mt-0.5">{step.by}</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
                {viewPR.rejection_reason && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    <span className="font-semibold">Rejection Reason: </span>{viewPR.rejection_reason}
                  </div>
                )}
              </div>

              {/* Line Items + Qty Check */}
              {viewLines.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-[#6B7280] uppercase mb-2">Line Items & Budget Check</div>
                  <table className="w-full text-xs border border-[#E5E7EB] rounded-lg overflow-hidden">
                    <thead className="bg-[#F9FAFB]">
                      <tr>
                        <th className="text-left px-3 py-2 text-[#6B7280]">Description</th>
                        <th className="text-left px-3 py-2 text-[#6B7280]">Sub Code</th>
                        <th className="text-left px-3 py-2 text-[#6B7280]">Cost Code</th>
                        <th className="text-left px-3 py-2 text-purple-700">Resource Code</th>
                        <th className="text-center px-3 py-2 text-[#6B7280]">Unit</th>
                        <th className="text-right px-3 py-2 text-[#6B7280]">PR Qty</th>
                        <th className="text-right px-3 py-2 text-[#6B7280]">Budget Qty</th>
                        <th className="text-right px-3 py-2 text-[#6B7280]">Committed</th>
                        <th className="text-right px-3 py-2 text-[#6B7280]">Remaining</th>
                        <th className="text-center px-3 py-2 text-[#6B7280]">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6] bg-white">
                      {viewLines.map((l, i) => {
                        const qc = l.sub_code ? viewQtyCheck[normSC(l.sub_code)] : null
                        const prQty = parseFloat(l.est_qty) || 0
                        // 'direct'/'indirect' are the current resolveHierarchyKey()
                        // kinds (replacing the old resolveDirectKey()-only
                        // 'resource'/'legacy' terminology) -- both are valid
                        // mapped states with real budget/committed/remaining
                        // values; only 'unknown' has none.
                        const isMapped = qc && (qc.kind === 'direct' || qc.kind === 'indirect')
                        const remaining = isMapped ? qc.remaining : null
                        const ok = remaining === null || prQty <= remaining
                        return (
                          <tr key={i} className={(isMapped && !ok) ? 'bg-red-50' : ''}>
                            <td className="px-3 py-2">{l.description}</td>
                            <td className="px-3 py-2 font-mono text-[#1B3A6B]">{l.sub_code || '—'}</td>
                            <td className="px-3 py-2 font-mono text-[#6B7280] text-xs">{budgetSubCodes.find(s=>normSC(s.code)===normSC(l.sub_code))?.costCode || '—'}</td>
                            <td className="px-3 py-2 font-mono text-purple-700 text-xs">{budgetSubCodes.find(s=>normSC(s.code)===normSC(l.sub_code))?.resourceCode || '—'}</td>
                            <td className="px-3 py-2 text-center text-[#9CA3AF]">{l.unit || '—'}</td>
                            <td className="px-3 py-2 text-right font-semibold">{fmtN(l.est_qty)}</td>
                            <td className="px-3 py-2 text-right text-[#1A7A4A]">{isMapped ? fmtN(qc.budget_qty) : '—'}</td>
                            <td className="px-3 py-2 text-right text-amber-600">{isMapped ? fmtN(qc.committed_qty) : '—'}</td>
                            <td className="px-3 py-2 text-right font-semibold">{isMapped ? fmtN(qc.remaining) : '—'}</td>
                            <td className="px-3 py-2 text-center">
                              {!qc || qc.kind === 'unknown' ? (
                                <span className="text-[#6B7280] font-semibold text-xs">No Mapping</span>
                              ) : ok ? (
                                <span className="text-green-600 font-semibold">✓ OK</span>
                              ) : (
                                <span className="text-red-600 font-semibold">⚠ Over</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Approval actions inside view */}
              {(() => {
                const btns = getApprovalButtons(viewPR)
                if (!btns.length) return null
                return (
                  <div className="flex gap-2 pt-2 border-t border-[#E5E7EB]">
                    {btns.map(btn => (
                      <button key={btn.action} onClick={() => {
                        if (btn.action === 'reject') { setRejectTarget(viewPR); setRejectReason('') }
                        else handleApprovalAction(viewPR, btn.action)
                      }}
                        className={`px-4 py-2 text-sm rounded-lg font-medium ${btn.color}`}>
                        {btn.label}
                      </button>
                    ))}
                  </div>
                )
              })()}

              {/* Phase C1: Edit entry point added here to match Equipment's
                  View Modal (eqViewPR), which already had this. Same gating
                  as the main list's Edit button (site_engineer, draft/rejected). */}
              {can('PR.Edit') && ['draft', 'rejected'].includes(viewPR.approval_stage || 'draft') && (
                <div className="flex gap-2 pt-2 border-t border-[#E5E7EB]">
                  <button onClick={() => { openEdit(viewPR); setViewPR(null) }}
                    className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB]">Edit</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Add/Edit Modal ──────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-bold text-[#1C1A18]">{editPR ? 'Edit PR' : 'New Purchase Requisition'}</h2>
              <button onClick={() => setShowModal(false)} className="text-[#9CA3AF] hover:text-[#1C1A18] text-xl">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Description *</label>
                <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 resize-none"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Requested By</label>
                  <input value={form.requested_by_name} onChange={e => setForm(p => ({...p, requested_by_name: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Department</label>
                  <input value={form.department} onChange={e => setForm(p => ({...p, department: e.target.value}))}
                    placeholder="Site / Civil / MEP…"
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none"/>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Required Date</label>
                  <input type="date" value={form.required_by} onChange={e => setForm(p => ({...p, required_by: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Priority</label>
                  <select value={form.priority} onChange={e => setForm(p => ({...p, priority: e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none">
                    {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Cost Code</label>
                  <div className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm bg-[#F9FAFB] text-[#9CA3AF] italic">
                    مشتق تلقائياً من Sub Code لكل بند
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Line Items</label>
                  <button onClick={() => setLines(l => [...l, { ...EMPTY_LINE }])}
                    className="text-xs text-[#1B3A6B] hover:underline">+ Add Line</button>
                </div>
                <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F9FAFB]">
                      <tr>
                        <th className="text-left px-3 py-2 text-[#6B7280]">Description</th>
                        <th className="text-left px-2 py-2 w-28 text-[#6B7280]">Sub Code</th>
                        <th className="text-left px-2 py-2 w-24 text-[#6B7280]">Cost Code</th>
                        <th className="text-left px-2 py-2 w-24 text-purple-700">Resource Code</th>
                        <th className="text-center px-2 py-2 w-16 text-[#6B7280]">Unit</th>
                        <th className="text-right px-2 py-2 w-20 text-[#6B7280]">Qty</th>
                        <th className="w-6 px-2 py-2"/>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6] bg-white">
                      {lines.map((line, i) => (
                        <tr key={i}>
                          <td className="px-2 py-1.5">
                            <input value={line.description || ''} onChange={e => setLines(l => l.map((x,j) => j===i?{...x,description:e.target.value}:x))}
                              className="w-full border-0 focus:outline-none text-xs bg-transparent"/>
                          </td>
                          <td className="px-2 py-1.5">
                            {/* Resolve the DB-normalized value (lowercase) back to the
                                original budgetSubCodes option value, so the <select>
                                correctly shows the selected Sub Code as selected --
                                HTML <select> matching is case-sensitive and cannot
                                match "mat-dew2" (DB) against "Mat-Dew2" (option) on
                                its own. */}
                            <select
                              value={budgetSubCodes.find(s => normSC(s.code) === normSC(line.sub_code))?.code || ''}
                              onChange={e => setLines(l => l.map((x,j) => j===i?{...x,sub_code:e.target.value}:x))}
                              className="w-full border-0 focus:outline-none text-xs bg-transparent font-mono text-[#1B3A6B]">
                              <option value="">— select —</option>
                              {budgetSubCodes.map(s => (
                                <option key={s.id} value={s.code}>{s.code} — {s.description}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5 text-xs font-mono text-[#6B7280]">
                            {budgetSubCodes.find(s => normSC(s.code) === normSC(line.sub_code))?.costCode || '—'}
                          </td>
                          <td className="px-2 py-1.5 text-xs font-mono text-purple-700">
                            {budgetSubCodes.find(s => normSC(s.code) === normSC(line.sub_code))?.resourceCode || '—'}
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              list={`unit-list-${i}`}
                              value={line.unit || ''}
                              onChange={e => setLines(l => l.map((x,j) => j===i?{...x,unit:e.target.value}:x))}
                              className="w-full border-0 focus:outline-none text-xs bg-transparent text-center"/>
                            <datalist id={`unit-list-${i}`}>
                              {['M','M2','M3','ML','KG','MT','TON','PC','SET','LOT','EACH','BAG','BOX','L','MONTH','DAY','LUMP SUM'].map(u => (
                                <option key={u} value={u}/>
                              ))}
                            </datalist>
                          </td>
                          <td className="px-2 py-1.5">
                            <input type="number" value={line.est_qty || ''} onChange={e => setLines(l => l.map((x,j) => j===i?{...x,est_qty:e.target.value}:x))}
                              className="w-full border-0 focus:outline-none text-xs bg-transparent text-right"/>
                          </td>
                          <td className="px-2 py-1.5">
                            {lines.length > 1 && (
                              <button onClick={() => setLines(l => l.filter((_,j) => j!==i))} className="text-red-400 hover:text-red-600">×</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none resize-none"/>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setShowModal(false)}
                className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#F9FAFB]">Cancel</button>
              <button onClick={handleSave} disabled={prSaving}
                className="flex-1 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#1B3A6B]/90 disabled:opacity-60">
                {prSaving ? 'جاري الحفظ...' : (editPR ? 'Save Changes' : 'Create PR')}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
        ) : (
          <>
            {/* ═══ Equipment PR Tab ═══ */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label:'Total',        value: eqStageCounts.all,        color:'text-[#0F1017]' },
                { label:'Draft',        value: eqStageCounts.draft,      color:'text-gray-500' },
                { label:'Pending Approval', value: eqStageCounts.cost_control + eqStageCounts.procurement, color:'text-amber-600' },
                { label:'Approved',     value: eqStageCounts.approved,    color:'text-green-600' },
              ].map(k => (
                <div key={k.label} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                  <div className={`text-2xl font-extrabold ${k.color}`}>{k.value}</div>
                  <div className="text-xs text-[#6B7280] mt-1">{k.label}</div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <input value={eqSearch} onChange={e=>setEqSearch(e.target.value)} placeholder="Search PR number or description..."
                className="flex-1 max-w-sm border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
              <select value={eqStageFilter} onChange={e=>setEqStageFilter(e.target.value)}
                className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
                {['all','draft','cost_control','procurement','approved','rejected','converted'].map(s=>(
                  <option key={s} value={s}>{s} ({eqStageCounts[s]})</option>
                ))}
              </select>
            </div>

            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
              {eqLoading ? (
                <div className="p-10 text-center text-[#6B7280]">Loading...</div>
              ) : eqFilteredPRs.length === 0 ? (
                <div className="p-10 text-center text-[#6B7280]">لا يوجد طلبات شراء معدات</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <th className="text-left px-4 py-2.5 font-medium text-[#6B7280]">PR Number</th>
                      <th className="text-left px-4 py-2.5 font-medium text-[#6B7280]">Description</th>
                      <th className="text-left px-4 py-2.5 font-medium text-[#6B7280]">Cost Code</th>
                      <th className="text-center px-4 py-2.5 font-medium text-[#6B7280]">Stage</th>
                      <th className="text-right px-4 py-2.5 font-medium text-[#6B7280]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eqFilteredPRs.map(pr => {
                      const stage = pr.approval_stage || 'draft'
                      const stageColors = { draft:'bg-gray-100 text-gray-700', cost_control:'bg-blue-100 text-blue-700', procurement:'bg-purple-100 text-purple-700', approved:'bg-green-100 text-green-700', rejected:'bg-red-100 text-red-700', converted:'bg-indigo-100 text-indigo-700' }
                      const btns = eqGetApprovalButtons(pr)
                      return (
                        <tr key={pr.id} className="border-b border-[#F3F4F6]">
                          <td className="px-4 py-3 font-semibold">{pr.pr_number}</td>
                          <td className="px-4 py-3">{pr.description}</td>
                          <td className="px-4 py-3 text-[#6B7280] font-mono text-xs">{eqDeriveCostCodeDisplay(pr)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${stageColors[stage]}`}>{stage}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1.5 justify-end flex-wrap">
                              <button onClick={()=>{ setEqViewPR(pr); const ls = eqLines[pr.id]||[]; setEqViewLines(ls); eqLoadQtyCheck(ls) }}
                                className="px-2.5 py-1 text-xs border border-gray-300 rounded-lg hover:bg-[#FAFAFA]">View</button>
                              <button onClick={()=>setEqPrintPR(pr.id)}
                                className="px-2.5 py-1 text-xs border border-[#1B3A6B] text-[#1B3A6B] rounded-lg hover:bg-[#EFF3F9]">🖨 Print</button>
                              {btns.map(b => (
                                <button key={b.action}
                                  onClick={()=> b.action==='reject' ? setEqRejectTarget(pr) : eqHandleApprovalAction(pr, b.action)}
                                  className={`px-2.5 py-1 text-xs rounded-lg font-medium ${b.color}`}>{b.label}</button>
                              ))}
                              {/* Bugfix: Edit was gated to 'draft'/'rejected' with
                                  no role check. Now also restricted to
                                  site_engineer, matching the Material PR pattern
                                  and the confirmed role-to-responsibility mapping
                                  (site_engineer owns Create/Submit/Resubmit). */}
                              {can('EquipmentPR.Edit') && ['draft','rejected'].includes(stage) && (
                                <button onClick={()=>eqOpenEdit(pr)} className="px-2.5 py-1 text-xs border border-gray-300 rounded-lg hover:bg-[#FAFAFA]">Edit</button>
                              )}
                              {/* Bugfix: Delete now also restricted to
                                  site_engineer (was draft-only with no role
                                  check). Server-side draft-only enforcement is
                                  provided by equipment_pr_delete_guard; role
                                  enforcement deferred until real useAuth()
                                  migration for this screen. */}
                              {can('EquipmentPR.Delete') && stage === 'draft' && (
                                <button onClick={()=>setEqConfirmDelete(pr.id)} className="px-2.5 py-1 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50">Delete</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {/* Equipment Reject Modal */}
      {eqRejectTarget && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:400}}>
            <h3 style={{fontWeight:700,marginBottom:12}}>سبب الرفض</h3>
            <textarea value={eqRejectReason} onChange={e=>setEqRejectReason(e.target.value)} rows={3}
              style={{width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:8,fontSize:13}} placeholder="اكتب السبب..." />
            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button onClick={()=>{setEqRejectTarget(null); setEqRejectReason('')}} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">إلغاء</button>
              <button onClick={async ()=>{ await eqHandleApprovalAction(eqRejectTarget,'reject',eqRejectReason); setEqRejectTarget(null); setEqRejectReason('') }}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm">تأكيد الرفض</button>
            </div>
          </div>
        </div>
      )}

      {/* Equipment Delete Confirm */}
      {eqConfirmDelete && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:360}}>
            <h3 style={{fontWeight:700,marginBottom:8}}>تأكيد الحذف</h3>
            <p style={{fontSize:13,color:'#6B7280',marginBottom:16}}>هل أنت متأكد من حذف هذا الطلب؟</p>
            <div style={{display:'flex',gap:8}}>
              <button onClick={()=>setEqConfirmDelete(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">إلغاء</button>
              <button onClick={()=>eqHandleDelete(eqConfirmDelete)} className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm">حذف</button>
            </div>
          </div>
        </div>
      )}

      {/* Equipment New/Edit Modal */}
      {eqShowModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:20}}>
          <div style={{background:'#fff',borderRadius:12,width:'100%',maxWidth:760,maxHeight:'90vh',overflow:'auto'}}>
            <div style={{padding:'16px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <h3 style={{fontWeight:700,fontSize:16}}>{eqEditPR ? 'Edit' : 'New'} Equipment PR</h3>
              <button onClick={()=>setEqShowModal(false)}>✕</button>
            </div>
            <div style={{padding:24,display:'flex',flexDirection:'column',gap:16}}>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Description *</label>
                <textarea value={eqForm.description} onChange={e=>setEqForm(f=>({...f,description:e.target.value}))} rows={2}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Department</label>
                  <input value={eqForm.department} onChange={e=>setEqForm(f=>({...f,department:e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Requested By</label>
                  <input value={eqForm.requested_by_name} onChange={e=>setEqForm(f=>({...f,requested_by_name:e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Required By</label>
                  <input type="date" value={eqForm.required_by} onChange={e=>setEqForm(f=>({...f,required_by:e.target.value}))}
                    className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-xs font-semibold text-[#6B7280] uppercase">Equipment Lines</label>
                  <button onClick={eqAddLine} className="text-xs text-orange-600 font-medium">+ Add Line</button>
                </div>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#F9FAFB]">
                      <th className="text-left px-2 py-1.5">Sub Code</th>
                      <th className="text-left px-2 py-1.5">Description</th>
                      <th className="text-right px-2 py-1.5">Est. Qty</th>
                      <th className="text-left px-2 py-1.5">Unit</th>
                      <th className="text-left px-2 py-1.5">Cost Code</th>
                      <th className="text-left px-2 py-1.5 text-purple-700">Resource Code</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {eqLinesForm.map((l, idx) => (
                      <tr key={idx} className="border-b border-[#F3F4F6]">
                        <td className="p-1">
                          <select value={l.sub_code_id||''} onChange={e=>{
                              const sc = eqSubCodes.find(s=>s.id===e.target.value)
                              eqHLine(idx,'sub_code_id',e.target.value)
                              eqHLine(idx,'equipment_type', sc ? sc.code : '')
                              eqHLine(idx,'sub_code', sc ? sc.code : '')
                              eqHLine(idx,'cost_code_id', sc ? (sc.costCodeId||'') : '')
                            }}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs">
                            <option value="">— Select Sub Code —</option>
                            {eqSubCodes.map(s=><option key={s.id} value={s.id}>{s.code} — {s.description}</option>)}
                          </select>
                        </td>
                        <td className="p-1">
                          <input value={l.description} onChange={e=>eqHLine(idx,'description',e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs" />
                        </td>
                        <td className="p-1">
                          <input type="number" value={l.est_qty} onChange={e=>eqHLine(idx,'est_qty',e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs text-right" />
                        </td>
                        <td className="p-1">
                          <input list={`eq-unit-list-${idx}`} value={l.unit} onChange={e=>eqHLine(idx,'unit',e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs" />
                          <datalist id={`eq-unit-list-${idx}`}>
                            {['M','M2','M3','ML','KG','MT','TON','PC','SET','LOT','EACH','BAG','BOX','L','MONTH','DAY','LUMP SUM'].map(u => (
                              <option key={u} value={u}/>
                            ))}
                          </datalist>
                        </td>
                        <td className="p-1 text-xs font-mono text-[#1A7A4A] font-semibold px-2">
                          {eqSubCodes.find(s=>s.id===l.sub_code_id)?.costCode || eqCostCodes.find(c=>c.id===l.cost_code_id)?.code || '—'}
                        </td>
                        <td className="p-1 text-xs font-mono text-purple-700 px-2">
                          {eqSubCodes.find(s=>s.id===l.sub_code_id)?.resourceCode || '—'}
                        </td>
                        <td className="p-1 text-center">
                          <button onClick={()=>eqRemoveLine(idx)} className="text-red-500">✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase">Notes</label>
                <textarea value={eqForm.notes} onChange={e=>setEqForm(f=>({...f,notes:e.target.value}))} rows={2}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm resize-none" />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={()=>setEqShowModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#F9FAFB]">Cancel</button>
              <button onClick={eqHandleSave} disabled={eqPrSaving} className="flex-1 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-60">
                {eqPrSaving ? 'جاري الحفظ...' : (eqEditPR ? 'Save Changes' : 'Create Equipment PR')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Equipment View Panel (slide-in) */}
      {eqViewPR && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB] sticky top-0 bg-white z-10">
              <div>
                <div className="font-mono text-xs text-orange-600 font-bold">{eqViewPR.pr_number}</div>
                <h2 className="text-lg font-bold text-[#1C1A18]">{eqViewPR.description}</h2>
              </div>
              <div className="flex items-center gap-2">
                <StageBadge stage={eqViewPR.approval_stage || 'draft'}/>
                <button onClick={() => setEqPrintPR(eqViewPR.id)}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600">🖨 Print</button>
                <button onClick={() => setEqViewPR(null)} className="text-[#9CA3AF] hover:text-[#1C1A18] text-xl">✕</button>
              </div>
            </div>
            <div className="p-5 space-y-5">
              {/* Meta */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                {[
                  ['Requested By', eqViewPR.requested_by_name],
                  ['Department',   eqViewPR.department],
                  ['Required Date',eqViewPR.required_by],
                  ['Cost Code',    eqViewPR.cost_codes ? `${eqViewPR.cost_codes.code} — ${eqViewPR.cost_codes.description}` : null],
                  ['Created',      eqViewPR.created_at ? new Date(eqViewPR.created_at).toLocaleDateString('en-GB') : null],
                  ['Notes',        eqViewPR.notes],
                ].map(([k, v]) => v ? (
                  <div key={k}>
                    <div className="text-xs text-[#9CA3AF] uppercase">{k}</div>
                    <div className="font-medium text-[#1C1A18] text-sm">{v}</div>
                  </div>
                ) : null)}
              </div>

              {/* Approval Trail */}
              <div className="bg-[#F9FAFB] rounded-xl p-4">
                <div className="text-xs font-semibold text-[#6B7280] uppercase mb-3">Approval Trail</div>
                <div className="flex items-center gap-2 flex-wrap">
                  {[
                    { key: 'cost_control', label: 'Cost Control', by: eqViewPR.cc_approved_by,   at: eqViewPR.cc_approved_at },
                    { key: 'procurement',  label: 'Procurement',  by: eqViewPR.proc_approved_by, at: eqViewPR.proc_approved_at },
                  ].map((step, i) => {
                    const stage = eqViewPR.approval_stage || 'draft'
                    const stages = ['draft', 'cost_control', 'procurement', 'approved', 'rejected', 'converted']
                    const stageIdx = stages.indexOf(stage)
                    const stepIdx  = stages.indexOf(step.key)
                    const done     = step.by || stageIdx > stepIdx
                    const current  = stage === step.key
                    return (
                      <div key={step.key} className="flex items-center gap-2">
                        {i > 0 && <div className="w-8 h-px bg-[#E5E7EB]"/>}
                        <div className={`flex flex-col items-center px-3 py-2 rounded-lg border text-xs
                          ${done ? 'bg-green-50 border-green-200' : current ? 'bg-amber-50 border-amber-200' : 'bg-white border-[#E5E7EB]'}`}>
                          <span className={`font-semibold ${done ? 'text-green-700' : current ? 'text-amber-700' : 'text-[#9CA3AF]'}`}>
                            {done ? '✓' : current ? '⏳' : '○'} {step.label}
                          </span>
                          {step.by && <span className="text-[10px] text-[#6B7280] mt-0.5">{step.by}</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
                {eqViewPR.rejection_reason && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    <span className="font-semibold">Rejection Reason: </span>{eqViewPR.rejection_reason}
                  </div>
                )}
              </div>

              {/* Equipment Line Items + Budget Check */}
              {eqViewLines.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-[#6B7280] uppercase mb-2">Line Items & Budget Check</div>
                  <table className="w-full text-xs border border-[#E5E7EB] rounded-lg overflow-hidden">
                    <thead className="bg-[#F9FAFB]">
                      <tr>
                        <th className="text-left px-3 py-2 text-[#6B7280]">Description</th>
                        <th className="text-left px-3 py-2 text-[#6B7280]">Sub Code</th>
                        <th className="text-center px-3 py-2 text-[#6B7280]">Unit</th>
                        <th className="text-right px-3 py-2 text-[#6B7280]">PR Qty</th>
                        <th className="text-right px-3 py-2 text-[#6B7280]">Budget Qty</th>
                        <th className="text-right px-3 py-2 text-[#6B7280]">Committed</th>
                        <th className="text-right px-3 py-2 text-[#6B7280]">Remaining</th>
                        <th className="text-center px-3 py-2 text-[#6B7280]">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6] bg-white">
                      {eqViewLines.map((l, i) => {
                        const scKey = l.sub_code || l.equipment_type
                        const qc = scKey ? eqViewQtyCheck[normSC(scKey)] : null
                        const prQty = parseFloat(l.est_qty) || 0
                        const isMapped = qc && (qc.kind === 'direct' || qc.kind === 'indirect')
                        const remaining = isMapped ? qc.remaining : null
                        const ok = remaining === null || prQty <= remaining
                        return (
                          <tr key={i} className={(isMapped && !ok) ? 'bg-red-50' : ''}>
                            <td className="px-3 py-2">{l.description || l.equipment_type}</td>
                            <td className="px-3 py-2 font-mono text-orange-600">{scKey || '—'}</td>
                            <td className="px-3 py-2 text-center text-[#9CA3AF]">{l.unit || '—'}</td>
                            <td className="px-3 py-2 text-right font-semibold">{fmtN(l.est_qty)}</td>
                            <td className="px-3 py-2 text-right text-[#1A7A4A]">{isMapped ? fmtN(qc.budget_qty) : '—'}</td>
                            <td className="px-3 py-2 text-right text-amber-600">{isMapped ? fmtN(qc.committed_qty) : '—'}</td>
                            <td className="px-3 py-2 text-right font-semibold">{isMapped ? fmtN(qc.remaining) : '—'}</td>
                            <td className="px-3 py-2 text-center">
                              {!qc || qc.kind === 'unknown' ? (
                                <span className="text-[#6B7280] font-semibold text-xs">No Mapping</span>
                              ) : ok ? (
                                <span className="text-green-600 font-semibold">✓ OK</span>
                              ) : (
                                <span className="text-red-600 font-semibold">⚠ Over</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Approval actions inside view */}
              {(() => {
                const btns = eqGetApprovalButtons(eqViewPR)
                if (!btns.length) return null
                return (
                  <div className="flex gap-2 pt-2 border-t border-[#E5E7EB]">
                    {btns.map(btn => (
                      <button key={btn.action} onClick={() => {
                        if (btn.action === 'reject') { setEqRejectTarget(eqViewPR); setEqRejectReason('') }
                        else eqHandleApprovalAction(eqViewPR, btn.action)
                      }}
                        className={`px-4 py-2 text-sm rounded-lg font-medium ${btn.color}`}>
                        {btn.label}
                      </button>
                    ))}
                  </div>
                )
              })()}

              {/* Bugfix: same Edit widening as the main list above (draft +
                  rejected), now also gated to site_engineer. */}
              {can('EquipmentPR.Edit') && ['draft','rejected'].includes(eqViewPR.approval_stage||'draft') && (
                <div className="flex gap-2 pt-2 border-t border-[#E5E7EB]">
                  <button onClick={()=>{eqOpenEdit(eqViewPR); setEqViewPR(null)}}
                    className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB]">Edit</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {printPR && <PrintDocument docType="pr" docId={printPR} onClose={()=>setPrintPR(null)}/>}
      {eqPrintPR && <PrintDocument docType="equipment_pr" docId={eqPrintPR} onClose={()=>setEqPrintPR(null)}/>}
    </div>
  )
}
